import React, { useState, useEffect, useCallback } from "react";
import Icon from "./Icon";
// NOTE: this file lives at src/components/ai/SettingsModal.jsx (same
// folder as AiSidebar.js) — adjust if your layout differs.
import { authenticationApi } from "../../features/authentication";
import { useFavorites } from "../../features/favorites/FavoritesContext";

export default function SettingsModal({ open, onClose, user, theme, onToggleTheme }) {
    const [activeTab, setActiveTab] = useState("profile");
    const [fullName, setFullName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [hasPassword, setHasPassword] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarError, setAvatarError] = useState("");

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [passwordSaved, setPasswordSaved] = useState(false);

    const { toggleFavorite } = useFavorites();
    const [favorites, setFavorites] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(false);

    // Pull the verified profile from the backend whenever the modal opens —
    // the JWT only encodes email, so name/avatar/hasPassword have to come
    // from here. Falls back silently to whatever we already have from the
    // token if the request fails, so the modal is never blank.
    useEffect(() => {
        if (!open) return;
        setError("");
        setSaved(false);
        setLoading(true);

        authenticationApi.getCurrentUser()
            .then((data) => {
                setFullName(data?.fullName || user?.name || "");
                setEmail(data?.email || user?.email || "");
                setAvatarUrl(data?.avatarUrl || null);
                setHasPassword(data?.hasPassword ?? true);
            })
            .catch(() => { /* keep JWT-derived defaults */ })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    // Saved Properties tab loads lazily, the first time it's opened.
    useEffect(() => {
        if (!open || activeTab !== "saved" || favorites.length > 0) return;
        setFavoritesLoading(true);
        authenticationApi.listFavorites()
            .then((list) => setFavorites(list || []))
            .catch(() => setFavorites([]))
            .finally(() => setFavoritesLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, activeTab]);

    const handleSave = useCallback(async () => {
        const trimmed = fullName.trim();
        if (!trimmed) {
            setError("Full name is required");
            return;
        }
        setSaving(true);
        setError("");
        setSaved(false);
        try {
            await authenticationApi.updateProfile(trimmed);
            setSaved(true);
        } catch (err) {
            setError(err.message || "Could not save changes. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [fullName]);

    const handleAvatarChange = useCallback(async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file next time
        if (!file) return;

        setAvatarError("");
        setAvatarUploading(true);
        try {
            const data = await authenticationApi.uploadAvatar(file);
            setAvatarUrl(data?.avatarUrl || null);
        } catch (err) {
            setAvatarError(err.message || "Could not upload image. Please try again.");
        } finally {
            setAvatarUploading(false);
        }
    }, []);

    const handlePasswordSave = useCallback(async () => {
        setPasswordError("");
        setPasswordSaved(false);

        if (hasPassword && !currentPassword) {
            setPasswordError("Enter your current password");
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError("New password must be at least 8 characters");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords don't match");
            return;
        }

        setPasswordSaving(true);
        try {
            await authenticationApi.changePassword(currentPassword, newPassword);
            setPasswordSaved(true);
            setHasPassword(true);
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setPasswordError(err.message || "Could not update password. Please try again.");
        } finally {
            setPasswordSaving(false);
        }
    }, [hasPassword, currentPassword, newPassword, confirmPassword]);

    const handleRemoveFavorite = useCallback(async (propertyId) => {
        setFavorites((prev) => prev.filter((p) => p.id !== propertyId));
        await toggleFavorite(propertyId);
    }, [toggleFavorite]);

    if (!open) return null;

    const initials = (fullName || user?.name || "U").charAt(0).toUpperCase();

    return (
        <div style={styles.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={styles.modal} role="dialog" aria-modal="true" aria-label="Settings">
                <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>

                <div style={styles.nav}>
                    <div style={styles.navLabel}>Settings</div>
                    <button
                        type="button"
                        style={{ ...styles.navItem, ...(activeTab === "profile" ? styles.navItemActive : {}) }}
                        onClick={() => setActiveTab("profile")}
                    >
                        <Icon name="settings" size={16} />
                        Profile
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.navItem, ...(activeTab === "security" ? styles.navItemActive : {}) }}
                        onClick={() => setActiveTab("security")}
                    >
                        <Icon name="lock" size={16} />
                        Security
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.navItem, ...(activeTab === "saved" ? styles.navItemActive : {}) }}
                        onClick={() => setActiveTab("saved")}
                    >
                        <Icon name="heart" size={16} />
                        Saved Properties
                    </button>
                    <button
                        type="button"
                        style={{ ...styles.navItem, ...(activeTab === "preferences" ? styles.navItemActive : {}) }}
                        onClick={() => setActiveTab("preferences")}
                    >
                        <Icon name="sun" size={16} />
                        Preferences
                    </button>
                </div>

                <div style={styles.content}>
                    {activeTab === "profile" && (
                        <>
                            <h2 style={styles.heading}>Profile</h2>

                            <div style={styles.row}>
                                <span style={styles.label}>Avatar</span>
                                <label style={styles.avatarWrap}>
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="" style={styles.avatarImg} />
                                    ) : (
                                        <div style={styles.avatar}>{initials}</div>
                                    )}
                                    <span style={styles.avatarEditBadge}>
                                        {avatarUploading ? "…" : <Icon name="edit" size={10} />}
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleAvatarChange}
                                        disabled={avatarUploading}
                                        style={styles.avatarInput}
                                    />
                                </label>
                            </div>
                            {avatarError && <p style={styles.error}>{avatarError}</p>}

                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="settings-fullname">Full name</label>
                                <input
                                    id="settings-fullname"
                                    style={styles.input}
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    disabled={loading}
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="settings-email">Email</label>
                                <input
                                    id="settings-email"
                                    style={{ ...styles.input, ...styles.inputDisabled }}
                                    value={email}
                                    disabled
                                />
                            </div>

                            {error && <p style={styles.error}>{error}</p>}
                            {saved && <p style={styles.success}>Saved.</p>}

                            <button
                                type="button"
                                style={{ ...styles.saveBtn, opacity: saving || loading ? 0.7 : 1 }}
                                onClick={handleSave}
                                disabled={saving || loading}
                            >
                                {saving ? "Saving…" : "Save changes"}
                            </button>
                        </>
                    )}

                    {activeTab === "security" && (
                        <>
                            <h2 style={styles.heading}>Security</h2>

                            {!hasPassword && (
                                <p style={styles.hint}>
                                    Your account doesn't have a password yet (you signed in via Google
                                    or a one-time code). Set one below if you'd also like to sign in
                                    with a password.
                                </p>
                            )}

                            {hasPassword && (
                                <div style={styles.field}>
                                    <label style={styles.label} htmlFor="settings-current-password">Current password</label>
                                    <input
                                        id="settings-current-password"
                                        type="password"
                                        style={styles.input}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                    />
                                </div>
                            )}

                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="settings-new-password">New password</label>
                                <input
                                    id="settings-new-password"
                                    type="password"
                                    style={styles.input}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="settings-confirm-password">Confirm new password</label>
                                <input
                                    id="settings-confirm-password"
                                    type="password"
                                    style={styles.input}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                            </div>

                            {passwordError && <p style={styles.error}>{passwordError}</p>}
                            {passwordSaved && <p style={styles.success}>Password updated.</p>}

                            <button
                                type="button"
                                style={{ ...styles.saveBtn, opacity: passwordSaving ? 0.7 : 1 }}
                                onClick={handlePasswordSave}
                                disabled={passwordSaving}
                            >
                                {passwordSaving ? "Updating…" : hasPassword ? "Update password" : "Set password"}
                            </button>
                        </>
                    )}

                    {activeTab === "saved" && (
                        <>
                            <h2 style={styles.heading}>Saved Properties</h2>

                            {favoritesLoading && <p style={styles.hint}>Loading…</p>}

                            {!favoritesLoading && favorites.length === 0 && (
                                <p style={styles.hint}>
                                    Properties you save (tap the heart icon on any listing) will show up here.
                                </p>
                            )}

                            {favorites.map((p) => (
                                <div key={p.id} style={styles.favoriteRow}>
                                    <div style={styles.favoriteThumbWrap}>
                                        {(p.mainImages?.[0] || p.images?.[0] || p.image) ? (
                                            <img
                                                src={p.mainImages?.[0] || p.images?.[0] || p.image}
                                                alt=""
                                                style={styles.favoriteThumb}
                                            />
                                        ) : (
                                            <div style={styles.favoriteThumbPlaceholder} />
                                        )}
                                    </div>
                                    <div style={styles.favoriteInfo}>
                                        <div style={styles.favoriteTitle}>{p.title}</div>
                                        <div style={styles.favoriteMeta}>
                                            {[p.location, p.price].filter(Boolean).join(" · ")}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        style={styles.favoriteRemoveBtn}
                                        onClick={() => handleRemoveFavorite(p.id)}
                                        aria-label="Remove from saved properties"
                                    >
                                        <Icon name="trash" size={14} />
                                    </button>
                                </div>
                            ))}
                        </>
                    )}

                    {activeTab === "preferences" && (
                        <>
                            <h2 style={styles.heading}>Preferences</h2>

                            <div style={styles.row}>
                                <span style={styles.label}>Appearance</span>
                                <div style={styles.themeToggle}>
                                    <button
                                        type="button"
                                        style={{ ...styles.themeBtn, ...(theme !== "dark" ? styles.themeBtnActive : {}) }}
                                        onClick={() => theme === "dark" && onToggleTheme?.()}
                                    >
                                        <Icon name="sun" size={14} /> Light
                                    </button>
                                    <button
                                        type="button"
                                        style={{ ...styles.themeBtn, ...(theme === "dark" ? styles.themeBtnActive : {}) }}
                                        onClick={() => theme !== "dark" && onToggleTheme?.()}
                                    >
                                        <Icon name="moon" size={14} /> Dark
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
    },
    modal: {
        position: "relative",
        display: "flex",
        width: "100%",
        maxWidth: 760,
        height: 540,
        maxHeight: "88vh",
        background: "#fff",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
    },
    closeBtn: {
        position: "absolute",
        top: 14,
        right: 14,
        width: 28,
        height: 28,
        borderRadius: "50%",
        border: "none",
        background: "#f1f5f9",
        color: "#64748b",
        fontSize: 12,
        cursor: "pointer",
        zIndex: 2,
    },
    nav: {
        width: 200,
        flexShrink: 0,
        background: "#f8fafc",
        borderRight: "1px solid #eef2f7",
        padding: "20px 10px",
        boxSizing: "border-box",
    },
    navLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        padding: "0 10px 10px",
    },
    navItem: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        width: "100%",
        padding: "9px 10px",
        border: "none",
        background: "none",
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: 500,
        color: "#475569",
        cursor: "pointer",
        textAlign: "left",
    },
    navItemActive: {
        background: "#e2e8f0",
        color: "#0f172a",
        fontWeight: 700,
    },
    content: {
        flex: 1,
        padding: "28px 32px",
        overflowY: "auto",
        boxSizing: "border-box",
    },
    heading: {
        fontSize: 17,
        fontWeight: 700,
        color: "#0f172a",
        margin: "0 0 20px",
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 0",
        borderBottom: "1px solid #f1f5f9",
    },
    field: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "14px 0",
        borderBottom: "1px solid #f1f5f9",
    },
    label: {
        fontSize: 13.5,
        fontWeight: 500,
        color: "#334155",
        flexShrink: 0,
    },
    hint: {
        fontSize: 13,
        color: "#64748b",
        lineHeight: 1.6,
        margin: "0 0 16px",
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #1B3A6B, #2563EB)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 15,
    },
    avatarWrap: {
        position: "relative",
        display: "inline-block",
        cursor: "pointer",
    },
    avatarImg: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        objectFit: "cover",
        display: "block",
    },
    avatarEditBadge: {
        position: "absolute",
        bottom: -2,
        right: -2,
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#0f172a",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 9,
        border: "2px solid #fff",
    },
    avatarInput: {
        position: "absolute",
        inset: 0,
        opacity: 0,
        cursor: "pointer",
        width: "100%",
        height: "100%",
    },
    input: {
        flex: 1,
        maxWidth: 320,
        padding: "9px 12px",
        borderRadius: 10,
        border: "1.5px solid #e2e8f0",
        fontSize: 13.5,
        color: "#0f172a",
        outline: "none",
        boxSizing: "border-box",
    },
    inputDisabled: {
        background: "#f8fafc",
        color: "#94a3b8",
        cursor: "not-allowed",
    },
    error: {
        color: "#ef4444",
        fontSize: 12.5,
        margin: "10px 0 0",
    },
    success: {
        color: "#16a34a",
        fontSize: 12.5,
        margin: "10px 0 0",
    },
    saveBtn: {
        marginTop: 20,
        padding: "10px 20px",
        background: "linear-gradient(135deg, #ff4d6d, #ff8fa3)",
        border: "none",
        borderRadius: 10,
        color: "#fff",
        fontWeight: 600,
        fontSize: 13.5,
        cursor: "pointer",
    },
    themeToggle: {
        display: "flex",
        gap: 6,
        background: "#f1f5f9",
        borderRadius: 10,
        padding: 4,
    },
    themeBtn: {
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 12px",
        border: "none",
        background: "none",
        borderRadius: 8,
        fontSize: 12.5,
        fontWeight: 600,
        color: "#64748b",
        cursor: "pointer",
    },
    themeBtnActive: {
        background: "#fff",
        color: "#0f172a",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    },
    favoriteRow: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 0",
        borderBottom: "1px solid #f1f5f9",
    },
    favoriteThumbWrap: {
        width: 52,
        height: 52,
        borderRadius: 10,
        overflow: "hidden",
        flexShrink: 0,
        background: "#f1f5f9",
    },
    favoriteThumb: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
    },
    favoriteThumbPlaceholder: {
        width: "100%",
        height: "100%",
        background: "#e2e8f0",
    },
    favoriteInfo: {
        flex: 1,
        minWidth: 0,
    },
    favoriteTitle: {
        fontSize: 13.5,
        fontWeight: 600,
        color: "#0f172a",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    favoriteMeta: {
        fontSize: 12,
        color: "#64748b",
        marginTop: 2,
    },
    favoriteRemoveBtn: {
        width: 30,
        height: 30,
        borderRadius: 8,
        border: "none",
        background: "#fef2f2",
        color: "#ef4444",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
    },
};
